/* eslint-disable react/prop-types */
/**
 * ConversationReport
 * A self-contained report viewer for structured conversation assessment data.
 * Designed to be embedded inside a parent component.
 *
 * Props:
 *   data  — the full report JSON object (see shape below)
 *
 * Styling uses CSS custom properties so it inherits your app's light/dark theme.
 * No external dependencies beyond React.
 */
// import React from "react";

const SAMPLE_DATA = {
  report: {
    highest_leverage_change:
      "Get a real agreement on the problem before you move to solutions — she needs to say it, not just nod at it.",
    readiness:
      "You walked into this conversation with a clear sense of what you wanted to happen, and that's actually part of the problem. The situation you described is real and worth addressing, but you framed it around your desired outcome rather than the actual problem. The evidence you're bringing is mostly impressions — things you've observed and interpreted — rather than specific, observable moments you can point to. If she pushes back, you don't have much to stand on.\n\nOn the counterpart side, you've thought about what she stands to gain, which is good, but you've underweighted what this feels like from where she sits. The good news: your sense of what will be hard for you in this conversation is accurate. You know you tend to move quickly past disagreement. That self-awareness is an asset if you use it.",
    strengths:
      "Your opening is solid. You named the purpose of the conversation clearly and gave her a reason to engage — that's not nothing, and a lot of people fumble it. Your instincts at exchange 4 were exactly right: you were thinking that you needed to slow down and understand her position before responding, and that was the right call. The fact that you knew that matters.\n\nYou also did a good job in the middle of the conversation of staying curious when she pushed back on the timeline — you asked a follow-up instead of defending, and that kept the conversation open when it could easily have closed.",
    what_happened:
      "The conversation started well and then stalled at the moment it needed to go deeper. You engaged her interest effectively in the opening — she was with you. The problem came when you hit the first real point of resistance and moved past it too quickly. She raised a concern about the approach, and instead of staying there and understanding it fully, you acknowledged it briefly and pivoted to your solution. That pivot felt like progress, but it wasn't — you hadn't actually resolved the disagreement, you'd just moved past it.\n\nThe result was a conversation that looked like it was heading toward agreement but wasn't. By the time you got to proposing a specific action, she gave you a yes that didn't have much behind it. The unresolved disagreement from the middle of the conversation was still there — it just went underground. That kind of agreement tends to fall apart in execution.",
    conversation_breakdown: [
      {
        phase_title: "The Opening",
        exchange_range: [1, 2],
        narrative:
          "You opened clearly and with purpose. You told her what the conversation was about and why it mattered — and importantly, you framed it in terms that connected to something she cares about, not just what you needed. She engaged. This was a good start and you should note it: getting someone genuinely engaged at the outset is a skill, and you did it well here.",
        key_moment: false,
        key_moment_note: null,
      },
      {
        phase_title: "The First Sign of Resistance",
        exchange_range: [3, 5],
        narrative:
          "Exchange 3 is where the conversation first got complicated. She raised a concern about the proposed timeline — not a hard no, but a real hesitation. Your inner monologue shows you noticed it: you were thinking that this felt like a deeper issue than the timeline itself, and you were right. But instead of following that instinct and asking what was underneath the concern, you addressed the surface of it and moved on. You gave her a reasonable answer about the timeline, and she accepted it — but the thing underneath it didn't get addressed.\n\nThis is the conversation's first hinge point. Not the place it broke, but the place where a different choice would have led somewhere better.",
        key_moment: true,
        key_moment_note:
          "The instinct to go deeper was right — following it here would have surfaced the real disagreement before it went underground.",
      },
      {
        phase_title: "The Premature Close",
        exchange_range: [9, 11],
        narrative:
          "You moved to a specific action item and she agreed to it. On the surface, this looks like a successful conclusion. But the agreement you got here is sitting on top of the unresolved disagreement from exchanges 3–5. She said yes to the action, but she didn't say — and you didn't ask — whether she was genuinely aligned on the problem it was meant to solve.\n\nThis is the conversation's critical moment. You led before you had a real agreement, and the yes you got reflects politeness and forward momentum more than genuine buy-in. Agreements like this tend to require renegotiation later.",
        key_moment: true,
        key_moment_note:
          "A real agreement on the problem needed to come before the action item — the yes here doesn't have enough behind it.",
      },
    ],
    debrief: [
      {
        priority: 1,
        change: "When you hit resistance, stay there longer before moving on.",
        picture:
          "At exchange 3, instead of answering her timeline concern and moving forward, try: 'I want to make sure I understand what's behind that — is the timeline itself the issue, or is there something about the overall approach that doesn't feel right to you yet?' Don't move to solutions until you've heard the answer.",
      },
      {
        priority: 2,
        change: "Get an explicit agreement on the problem before you propose a solution.",
        picture:
          "Before exchange 9, add something like: 'Before we talk about next steps, I want to make sure we're actually aligned on what we're solving for. Can you put in your own words what you think the real issue is?' Her answer will tell you whether you have a real agreement or a surface one.",
      },
      {
        priority: 3,
        change: "When you get a yes, test it.",
        picture:
          "After she agrees to the action item, ask: 'What would make this feel like it's working for you, six weeks from now?' A real yes can answer that question. A polite yes can't.",
      },
    ],
  },
  metadata: {
    assessment_id: "conv_sample_001",
    conversation_mode: "anticipated",
    primary_breakdown_stage: "Agree",
    stage_summary: {
      ready: { rating: "M", finding: "Outcome is clear but problem framing and evidence need sharpening." },
      engage: { rating: "H", finding: "Opened well and captured her interest effectively." },
      agree: { rating: "L", finding: "Moved past resistance without resolving the underlying disagreement." },
      lead: { rating: "L", finding: "Action item secured but the agreement beneath it was not genuine." },
    },
    all_scores: {
      ready: { composite: 3, problem_framing: 2, stakes_modeling: 3, evidence_quality: 2, challenge_accuracy: 4 },
      engage: { composite: 4, frame_quality: 4, express_effectiveness: 4 },
      agree: { composite: 2, explore_depth: 2, listen_quality: 3, agreement_authenticity: 1 },
      lead: { composite: 2, action_specificity: 2 },
      behavioral: { say_think_gap_severity: 3 },
    },
  },
};

// ─── helpers ──────────────────────────────────────────────────────────────────

// Stage performance: H = strong (green), M = moderate (yellow), L = breakdown (red)
function stageRatingColor(rating) {
  if (rating === "H") return { bg: "var(--color-background-success)", text: "var(--color-text-success)" };
  if (rating === "M") return { bg: "var(--color-background-warning)", text: "var(--color-text-warning)" };
  return { bg: "var(--color-background-danger)", text: "var(--color-text-danger)" };
}

function scoreColor(score) {
  if (score >= 4) return "var(--color-text-success)";
  if (score >= 3) return "var(--color-text-warning)";
  return "var(--color-text-danger)";
}

function scoreColorInverted(score) {
  if (score <= 2) return "var(--color-text-success)";
  if (score <= 3) return "var(--color-text-warning)";
  return "var(--color-text-danger)";
}

// ─── base UI ──────────────────────────────────────────────────────────────────

function SectionLabel({ children }) {
  return (
    <p style={{
      fontSize: 11, fontWeight: 500, letterSpacing: "0.07em",
      textTransform: "uppercase", color: "var(--color-text-secondary)",
      margin: "0 0 10px",
    }}>
      {children}
    </p>
  );
}

function Card({ children, style }) {
  return (
    <div style={{
      background: "var(--color-background-primary)",
      border: "0.5px solid var(--color-border-tertiary)",
      borderRadius: "var(--border-radius-lg)",
      padding: "1rem 1.25rem",
      ...style,
    }}>
      {children}
    </div>
  );
}

function Badge({ label, bg, fg }) {
  return (
    <span style={{
      display: "inline-flex", alignItems: "center",
      fontSize: 11, fontWeight: 500, padding: "2px 8px",
      borderRadius: 100, background: bg, color: fg, flexShrink: 0,
    }}>
      {label}
    </span>
  );
}

function ScoreRow({ label, score, inverted = false }) {
  const color = inverted ? scoreColorInverted(score) : scoreColor(score);
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", fontSize: 12.5 }}>
      <span style={{ color: "var(--color-text-secondary)" }}>{label}</span>
      <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
        <span style={{
          width: 8, height: 8, borderRadius: "50%", flexShrink: 0,
          background: color, display: "inline-block",
        }} />
        <span style={{ fontWeight: 500, color: "var(--color-text-primary)" }}>{score}</span>
      </div>
    </div>
  );
}

function Divider() {
  return <hr style={{ border: "none", borderTop: "0.5px solid var(--color-border-tertiary)", margin: 0 }} />;
}

// Splits \n\n into separate <p> tags so multi-paragraph prose renders correctly
function Prose({ text, style }) {
  const paragraphs = (text || "").split("\n\n").filter(Boolean);
  return (
    <>
      {paragraphs.map((p, i) => (
        <p key={i} style={{
          fontSize: 13.5, lineHeight: 1.75,
          color: "var(--color-text-secondary)",
          margin: i < paragraphs.length - 1 ? "0 0 0.75rem" : 0,
          ...style,
        }}>
          {p}
        </p>
      ))}
    </>
  );
}

// ─── stage grid ───────────────────────────────────────────────────────────────

function StageGrid({ stageSummary }) {
  const stages = [
    { key: "ready",  label: "Ready"  },
    { key: "engage", label: "Engage" },
    { key: "agree",  label: "Agree"  },
    { key: "lead",   label: "Lead"   },
  ];
  return (
    <div>
      <SectionLabel>Stage performance</SectionLabel>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: 8 }}>
        {stages.map(({ key, label }) => {
          const stage = stageSummary?.[key];
          if (!stage) return null;
          const colors = stageRatingColor(stage.rating);
          const isLow = stage.rating === "L";
          return (
            <Card
              key={key}
              style={{ borderColor: isLow ? "var(--color-border-danger)" : "var(--color-border-tertiary)" }}
            >
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
                <span style={{ fontSize: 12, fontWeight: 500, color: "var(--color-text-primary)" }}>{label}</span>
                <Badge label={stage.rating} bg={colors.bg} fg={colors.text} />
              </div>
              <p style={{ fontSize: 12, color: "var(--color-text-secondary)", margin: 0, lineHeight: 1.4 }}>
                {stage.finding}
              </p>
            </Card>
          );
        })}
      </div>
    </div>
  );
}

// ─── highest leverage ─────────────────────────────────────────────────────────

function HighestLeverage({ text }) {
  return (
    <div style={{
      background: "var(--color-background-danger)",
      border: "0.5px solid var(--color-border-danger)",
      borderRadius: "var(--border-radius-lg)",
      padding: "1rem 1.25rem",
    }}>
      <p style={{
        fontSize: 10, fontWeight: 500, letterSpacing: "0.07em",
        textTransform: "uppercase", color: "var(--color-text-danger)", margin: "0 0 6px",
      }}>
        Highest leverage change
      </p>
      <p style={{ fontSize: 14, fontWeight: 500, color: "var(--color-text-primary)", margin: 0, lineHeight: 1.55 }}>
        {text}
      </p>
    </div>
  );
}

// ─── what happened ────────────────────────────────────────────────────────────

function WhatHappened({ text }) {
  return (
    <div>
      <SectionLabel>What happened</SectionLabel>
      <Card style={{ border: "1px solid var(--color-border-secondary)" }}>
        <Prose text={text} />
      </Card>
    </div>
  );
}

// ─── readiness ────────────────────────────────────────────────────────────────

function Readiness({ text }) {
  return (
    <div>
      <SectionLabel>Readiness</SectionLabel>
      <Prose text={text} />
    </div>
  );
}

// ─── strengths ────────────────────────────────────────────────────────────────

function Strengths({ text }) {
  return (
    <div>
      <SectionLabel>What you&apos;re doing well</SectionLabel>
      <Card style={{
        background: "var(--color-background-success)",
        borderColor: "var(--color-border-success, var(--color-border-tertiary))",
      }}>
        <Prose text={text} />
      </Card>
    </div>
  );
}

// ─── conversation breakdown ───────────────────────────────────────────────────

function PhaseCard({ phase }) {
  const { phase_title, exchange_range, narrative, key_moment, key_moment_note } = phase;
  const [start, end] = exchange_range;
  const rangeLabel = start === end ? `Exchange ${start}` : `Exchanges ${start}–${end}`;

  return (
    <Card style={key_moment ? { borderColor: "var(--color-border-warning)", borderWidth: 1 } : {}}>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 10, gap: 8 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <span style={{ fontSize: 13, fontWeight: 500, color: "var(--color-text-primary)" }}>
            {phase_title}
          </span>
          {key_moment && (
            <Badge
              label="Key moment"
              bg="var(--color-background-warning)"
              fg="var(--color-text-warning)"
            />
          )}
        </div>
        <span style={{ fontSize: 11, color: "var(--color-text-secondary)", flexShrink: 0, paddingTop: 2 }}>
          {rangeLabel}
        </span>
      </div>

      <Prose text={narrative} />

      {key_moment && key_moment_note && (
        <div style={{
          marginTop: 12,
          padding: "8px 12px",
          background: "var(--color-background-warning)",
          borderRadius: "var(--border-radius-md)",
          borderLeft: "3px solid var(--color-text-warning)",
        }}>
          <p style={{ fontSize: 12.5, color: "var(--color-text-primary)", margin: 0, lineHeight: 1.5, fontStyle: "italic" }}>
            {key_moment_note}
          </p>
        </div>
      )}
    </Card>
  );
}

function ConversationBreakdown({ phases }) {
  return (
    <div>
      <SectionLabel>The conversation, moment by moment</SectionLabel>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {phases.map((phase) => (
          <PhaseCard key={phase.phase_title} phase={phase} />
        ))}
      </div>
    </div>
  );
}

// ─── debrief ──────────────────────────────────────────────────────────────────

const PRIORITY_COLORS = [
  { bg: "var(--color-background-danger)",   fg: "var(--color-text-danger)"   },
  { bg: "var(--color-background-warning)",  fg: "var(--color-text-warning)"  },
  { bg: "var(--color-background-secondary)", fg: "var(--color-text-secondary)" },
];

function Debrief({ items }) {
  const sorted = [...items].sort((a, b) => a.priority - b.priority);
  return (
    <div>
      <SectionLabel>Your debrief</SectionLabel>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {sorted.map((item) => {
          const colors = PRIORITY_COLORS[item.priority - 1] || PRIORITY_COLORS[2];
          return (
            <Card key={item.priority}>
              <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
                <div style={{
                  width: 22, height: 22, borderRadius: "50%",
                  background: colors.bg, color: colors.fg,
                  fontSize: 12, fontWeight: 500,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  flexShrink: 0, marginTop: 1,
                }}>
                  {item.priority}
                </div>
                <div style={{ minWidth: 0 }}>
                  <p style={{ fontSize: 13.5, fontWeight: 500, color: "var(--color-text-primary)", margin: "0 0 8px", lineHeight: 1.4 }}>
                    {item.change}
                  </p>
                  <div style={{
                    background: "var(--color-background-secondary)",
                    borderRadius: "var(--border-radius-md)",
                    padding: "10px 12px",
                  }}>
                    <p style={{
                      fontSize: 11, fontWeight: 500, letterSpacing: "0.05em",
                      textTransform: "uppercase", color: "var(--color-text-secondary)", margin: "0 0 4px",
                    }}>
                      Try this
                    </p>
                    <p style={{ fontSize: 13, color: "var(--color-text-secondary)", margin: 0, lineHeight: 1.6, fontStyle: "italic" }}>
                      {item.picture}
                    </p>
                  </div>
                </div>
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
}

// ─── scores grid ──────────────────────────────────────────────────────────────

function ScoresGrid({ scores }) {
  const sections = [
    scores.ready && {
      label: "Ready",
      rows: [
        { label: "Composite",          score: scores.ready.composite          },
        { label: "Problem framing",    score: scores.ready.problem_framing    },
        { label: "Stakes modeling",    score: scores.ready.stakes_modeling    },
        { label: "Evidence quality",   score: scores.ready.evidence_quality   },
        { label: "Challenge accuracy", score: scores.ready.challenge_accuracy },
      ],
    },
    scores.engage && {
      label: "Engage",
      rows: [
        { label: "Composite",             score: scores.engage.composite             },
        { label: "Frame quality",         score: scores.engage.frame_quality         },
        { label: "Express effectiveness", score: scores.engage.express_effectiveness },
      ],
    },
    scores.agree && {
      label: "Agree",
      rows: [
        { label: "Composite",              score: scores.agree.composite              },
        { label: "Explore depth",          score: scores.agree.explore_depth          },
        { label: "Listen quality",         score: scores.agree.listen_quality         },
        { label: "Agreement authenticity", score: scores.agree.agreement_authenticity },
      ],
    },
    (scores.lead || scores.behavioral) && {
      label: "Lead & Behavioral",
      rows: [
        scores.lead && { label: "Lead composite",     score: scores.lead.composite          },
        scores.lead && { label: "Action specificity", score: scores.lead.action_specificity },
        scores.behavioral && { label: "Say/think gap", score: scores.behavioral.say_think_gap_severity, inverted: true },
      ].filter(Boolean),
    },
  ].filter(Boolean);

  return (
    <div>
      <SectionLabel>Assessment scores</SectionLabel>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 8 }}>
        {sections.map((sec) => (
          <Card key={sec.label}>
            <p style={{
              fontSize: 11, fontWeight: 500, letterSpacing: "0.05em",
              textTransform: "uppercase", color: "var(--color-text-secondary)", margin: "0 0 10px",
            }}>
              {sec.label}
            </p>
            <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
              {sec.rows.map((r) => (
                <ScoreRow key={r.label} label={r.label} score={r.score} inverted={r.inverted || false} />
              ))}
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}

// ─── main component ───────────────────────────────────────────────────────────

export default function ConversationReport({ data = SAMPLE_DATA }) {
  // Normalize: old reports were stored as { ..., result: { report, metadata } };
  // new reports (and the polling path) deliver { report, metadata } directly.
  const normalized = data?.result?.report ? data.result : data;
  const { report, metadata } = normalized;

  if (!report?.what_happened) {
    return (
      <div style={{ fontFamily: "var(--font-sans)", color: "var(--color-text-secondary)", padding: "2rem", textAlign: "center" }}>
        <p style={{ margin: 0 }}>This report was generated with an earlier version and can no longer be displayed.</p>
      </div>
    );
  }

  const {
    highest_leverage_change,
    readiness,
    strengths,
    what_happened,
    conversation_breakdown,
    debrief,
    playbook,
  } = report;
  const debriefItems = debrief ?? playbook; // fallback for reports stored before the debrief rename
  const { stage_summary, all_scores } = metadata;

  return (
    <div style={{
      fontFamily: "var(--font-sans)",
      color: "var(--color-text-primary)",
      display: "flex",
      flexDirection: "column",
      gap: "1.5rem",
    }}>
      {stage_summary && <StageGrid stageSummary={stage_summary} />}
      {highest_leverage_change && <HighestLeverage text={highest_leverage_change} />}
      {what_happened && <WhatHappened text={what_happened} />}
      {(readiness || strengths) && <Divider />}
      {readiness && <Readiness text={readiness} />}
      {strengths && <Strengths text={strengths} />}
      {conversation_breakdown?.length > 0 && (
        <>
          <Divider />
          <ConversationBreakdown phases={conversation_breakdown} />
        </>
      )}
      {debriefItems?.length > 0 && (
        <>
          <Divider />
          <Debrief items={debriefItems} />
        </>
      )}
    </div>
  );
}
